import React from 'react';
import { cn } from '../../lib/utils';

/**
 * La tarjeta de un plan.
 *
 * Una sola para toda la app: la usan la sección de planes de la landing y el
 * selector con el que se paga (components/pricing-section.jsx, en /admin/plans
 * y en el paso de planes del onboarding).
 *
 * Estaba escrita dos veces, y como eran dos diseños distintos se veían como dos
 * productos: la landing con tarjetas blancas de esquinas grandes, precio bien
 * grande y una línea que separa los datos de la lista; el selector con otras
 * proporciones, botones negros y un check verde de otro tamaño. Con la lista de
 * planes ya unificada en data/planes.js, esto es la otra mitad del mismo
 * problema.
 *
 * Lo único que cambia entre los dos usos es qué botones van abajo —la landing
 * lleva a registrarse, el selector cobra—, así que eso entra por `children`.
 */

/** El tilde de las viñetas: círculo tenue del color de la marca. */
const Tilde = () => (
  <span
    className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10"
    aria-hidden="true"
  >
    <svg
      className="h-3 w-3 text-primary"
      fill="none"
      viewBox="0 0 12 12"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 6.5l2.5 2.5 4.5-5" />
    </svg>
  </span>
);

export default function TarjetaDePlan({ plan, children, className, resaltado }) {
  // `resaltado` permite marcarla desde afuera —por ejemplo, el plan que el
  // negocio ya tiene contratado— sin tocar los datos del plan.
  const destacado = resaltado ?? plan.destacado;

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-3xl border bg-white p-8',
        destacado ? 'border-primary shadow-lg ring-1 ring-primary' : 'border-neutral-200 shadow-sm',
        className
      )}
    >
      {plan.etiqueta && (
        <span
          className={cn(
            'absolute right-6 top-6 rounded-full px-3 py-1 text-xs font-semibold',
            destacado ? 'bg-primary text-primary-foreground' : 'bg-neutral-100 text-neutral-700'
          )}
        >
          {plan.etiqueta}
        </span>
      )}

      {/* El hueco de la derecha lo reserva la etiqueta, que va posicionada
          encima: sin él, en una tarjeta angosta "Business Pro Plus" quedaba
          tapado por "Todo incluido". */}
      <h3 className={cn('text-2xl font-bold text-neutral-900', plan.etiqueta && 'pr-24')}>
        {plan.nombre}
      </h3>
      <p className="mt-1 text-sm text-neutral-500">{plan.bajada}</p>

      <p className="mt-8 flex items-baseline gap-1">
        <span className="text-5xl font-bold tracking-tight text-neutral-900">
          {plan.precioTexto}
        </span>
        <span className="text-sm text-neutral-500">{plan.periodo}</span>
      </p>

      <hr className="my-8 border-neutral-200" />

      {/* `flex-1` en la lista: es lo que empuja los botones al pie y hace que
          todas las tarjetas de una fila los tengan a la misma altura, aunque
          una tenga seis viñetas y otra cuatro. */}
      <ul className="flex-1 space-y-4">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3">
            <Tilde />
            <span className="text-sm leading-relaxed text-neutral-700">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 space-y-3">{children}</div>
    </div>
  );
}
